// Vue Typing Indicator v2.1.1 - Fixed channel format
import { ref, onUnmounted } from 'vue';
import { useMessagesStore } from '../stores/messages';
import { getCurrentUserId } from '../utils/userUtils';

const TYPING_DEBOUNCE_MS = 500;
const TYPING_TIMEOUT_MS = 6000;
const RATE_LIMIT_MS = 3000;

let instance: { onInput: (chatId: string | number) => void; stopTyping: (chatId: string | number) => void; handleTypingEvent: (payload: any) => void } | null = null;
let unmountedRegistered = false;

export function useTypingIndicator() {
  if (instance) {
    return instance;
  }

  const store = useMessagesStore();
  const lastSentAt = ref(0);
  let debounceTimer: number | null = null;
  let timeoutTimer: number | null = null;
  const remoteTimers = new Map<string, number>();

  const sendTyping = (chatId: string | number, typingState: boolean = true) => {
    const now = Date.now();
    // Bypass rate-limit for stopTyping so remote sees immediate clear
    if (typingState && now - lastSentAt.value < RATE_LIMIT_MS) return;
    if (typingState) {
      lastSentAt.value = now;
    }

    const fromUserIdRaw = getCurrentUserId();
    const fromUserId = Number(fromUserIdRaw);
    if (!Number.isFinite(fromUserId) || fromUserId <= 0) {
      return;
    }
    
    // Отправляем напрямую через bootstrap.sendMessage как в AngularJS
    const w = window as any;
    const bootstrap = w.wsBootstrap;
    if (bootstrap && typeof bootstrap.sendMessage === 'function' && typeof bootstrap.isConnected === 'function') {
      if (!bootstrap.isConnected()) {
        return;
      }
      
      const payload = {
        type: 'typing',
        channel: 'typing',
        data: {
          contactId: chatId,
          from_user_id: fromUserId,
          isTyping: typingState
        }
      };
      
      bootstrap.sendMessage(payload);
    }
    
    if (typingState) {
      resetTimeout(chatId);
    }
  };

  const stopTyping = (chatId: string | number) => {
    clearTimeoutTimer();
    // Также очищаем debounce таймер чтобы предотвратить ложное срабатывание
    if (debounceTimer && typeof window !== 'undefined' && window.clearTimeout) {
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    // Отправляем событие stop typing через WebSocket
    sendTyping(chatId, false);
  };

  const onInput = (chatId: string | number) => {
    if (debounceTimer && typeof window !== 'undefined' && window.clearTimeout) {
      window.clearTimeout(debounceTimer);
    }
    if (typeof window !== 'undefined' && window.setTimeout) {
      debounceTimer = window.setTimeout(() => sendTyping(chatId), TYPING_DEBOUNCE_MS);
    }
  };

  const handleTypingEvent = (payload: any) => {
    const p = payload && typeof payload === 'object' ? payload : {};
    const data = p.data && typeof p.data === 'object' ? p.data : p;

    const chatId =
      data.contactId ??
      data.contact_id ??
      data.chatId ??
      data.chat_id ??
      data.conversationId ??
      data.conversation_id ??
      p.contactId ??
      p.contact_id ??
      p.chatId ??
      p.chat_id ??
      p.conversationId ??
      p.conversation_id;

    const from =
      data.from_user_id ??
      data.fromUserId ??
      data.userId ??
      data.user_id ??
      data.from ??
      p.from_user_id ??
      p.fromUserId ??
      p.userId ??
      p.user_id ??
      p.from;

    if (!chatId || !from) return;

    const remoteTyping =
      typeof data.isTyping === 'boolean'
        ? data.isTyping
        : typeof p.isTyping === 'boolean'
          ? p.isTyping
          : true;

    // Немедленная очистка при isTyping: false
    if (remoteTyping === false) {
      const key = `${chatId}:${from}`;
      const existing = remoteTimers.get(key);
      if (existing && typeof window !== 'undefined' && window.clearTimeout) {
        window.clearTimeout(existing);
        remoteTimers.delete(key);
      }
      
      if (store.typing[chatId] && store.typing[chatId][from]) {
        delete store.typing[chatId][from];
        if (store.typing[chatId] && Object.keys(store.typing[chatId]).length === 0) {
          delete store.typing[chatId];
        }
      }
      return;
    }

    store.typing[chatId] = store.typing[chatId] || {};
    (store.typing[chatId] as any)[from] = {
      isTyping: remoteTyping !== false,
      ts: Date.now(),
    };

    const key = `${chatId}:${from}`;
    const existing = remoteTimers.get(key);
    if (existing && typeof window !== 'undefined' && window.clearTimeout) {
      window.clearTimeout(existing);
    }

    if (typeof window !== 'undefined' && window.setTimeout) {
      remoteTimers.set(
        key,
        window.setTimeout(() => {
          const state = store.typing[chatId] && (store.typing[chatId] as any)[from];
          if (!state) {
            remoteTimers.delete(key);
            return;
          }

          const ts = Number((state as any).ts);
          if (!Number.isFinite(ts) || Date.now() - ts >= TYPING_TIMEOUT_MS) {
            delete store.typing[chatId][from];
            if (store.typing[chatId] && Object.keys(store.typing[chatId]).length === 0) {
              delete store.typing[chatId];
            }
          }

          remoteTimers.delete(key);
        }, TYPING_TIMEOUT_MS)
      );
    }
  };

  const resetTimeout = (chatId: string | number) => {
    clearTimeoutTimer();
    if (typeof window !== 'undefined' && window.setTimeout) {
      timeoutTimer = window.setTimeout(() => {
        stopTyping(chatId);
      }, TYPING_TIMEOUT_MS);
    }
  };

  const clearTimeoutTimer = () => {
    if (timeoutTimer && typeof window !== 'undefined' && window.clearTimeout) {
      window.clearTimeout(timeoutTimer);
      timeoutTimer = null;
    }
  };

  if (!unmountedRegistered) {
    unmountedRegistered = true;
    onUnmounted(() => {
      if (debounceTimer && typeof window !== 'undefined' && window.clearTimeout) {
        window.clearTimeout(debounceTimer);
      }
      clearTimeoutTimer();

      remoteTimers.forEach((t) => {
        if (typeof window !== 'undefined' && window.clearTimeout) {
          window.clearTimeout(t);
        }
      });
      remoteTimers.clear();
    });
  }

  instance = { onInput, stopTyping, handleTypingEvent };
  return instance;
}
